<template>
  <div>
    <b-row>
      <b-col md="6">
        <h2>
          {{ $t('Manage Users') }}
          <span v-if="currentTeam && currentTeam.name">- {{ currentTeam.name }}</span>
        </h2>
      </b-col>

      <b-col md="6" class="px-0">
        <b-row class="justiy-content-end align-items-center">
          <b-col md="8" cols="7">
            <b-form-group>
              <b-form-input v-model="searchFor" class="float-right" :placeholder="$t('Search')" />
            </b-form-group>
          </b-col>

          <b-col md="4" cols="5" class="px-0">
            <b-button v-b-modal.add-user-modal class="ml-2 mb-1" variant="primary">
              {{ $t('Add Member') }}
            </b-button>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-overlay :show="isProcessing">
      <b-table v-if="filteredMembers.length" :fields="fields" :items="filteredMembers" :responsive="IS_MOBILE()"
        show-empty>
        <template #cell(name)="row">
          <div :style="IS_MOBILE() ? 'width: 50vw;' : ''">
            <user-with-tags :user="row.item" />
          </div>
        </template>

        <template #cell(actions)="row">
          <b-button v-b-tooltip.top="$t('Remove Member')" variant="outline-danger" size="sm" class="p-1 btn-icon-sm"
            :disabled="isProcessing" @click="confirmRemove(row.item)">
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </template>
      </b-table>
    </b-overlay>

    <b-modal id="team-remove-member-modal"
      :title="selectedUser ? `Remove ${selectedUser.name} from team` : 'Remove user'" ok-title="Remove"
      ok-variant="danger" @ok="removeMember" @hidden="selectedUser = null">
      <span v-if="selectedUser">Are you sure you want to remove {{ selectedUser.name }} from this team?</span>
    </b-modal>

    <add-team :team-info="currentTeam" :members="membersForModal" @updateTeamCount="() => $emit('updated')" />
  </div>
</template>

<script setup>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BTable,
  BButton,
  BOverlay,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import { computed, getCurrentInstance, ref } from 'vue'
import useApollo from '@/plugins/graphql/useApollo'
import UserWithTags from '@/views/common/components/UserWithTags.vue'
import addTeam from '../CRUD/components/addTeam.vue'

const root = getCurrentInstance().proxy.$root

defineExpose({
  directives: {
    'b-tooltip': VBTooltip,
  },
})

const props = defineProps({
  teams: {
    type: [Object, Array],
    default: () => [],
  },
  currentTeam: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['updated'])

const searchFor = ref('')
const isProcessing = ref(false)
const selectedUser = ref(null)

const fields = [
  { label: root.$t('Team Member'), key: 'name', thClass: 'text-capitalize w-75' },
  { label: root.$t('Actions'), key: 'actions', thClass: 'text-capitalize w-25' },
]

const filteredMembers = computed(() => {
  const list = Array.isArray(props.teams) ? props.teams : []
  const key = (searchFor.value || '').trim().toLowerCase()
  return key ? list.filter(u => (u.name || '').toLowerCase().includes(key)) : list
})

const membersForModal = computed(() => {
  const fromTeam = props.currentTeam?.users?.data
  if (Array.isArray(fromTeam) && fromTeam.length) return fromTeam

  // addTeam.vue expects items with `uuid` for preselection.
  return (Array.isArray(props.teams) ? props.teams : [])
    .map(u => u?.uid)
    .filter(Boolean)
    .map(uid => ({ uuid: uid }))
})

const confirmRemove = (user) => {
  selectedUser.value = user
  root.$bvModal.show('team-remove-member-modal')
}

const removeMember = () => {
  if (!selectedUser.value) return

  isProcessing.value = true
  const remainingUserUids = (Array.isArray(props.teams) ? props.teams : [])
    .filter(u => u.uid !== selectedUser.value.uid)
    .map(u => u.uid)

  const projectUid = props.currentTeam?.project?.uuid || root.$store?.state?.project?.selectedProject
  const teamUid = props.currentTeam?.uuid

  if (!projectUid || !teamUid) {
    root.showErrorMessage({ data: { message: 'Missing team/project context. Please refresh the page and try again.' } })
    isProcessing.value = false
    return
  }

  // Reuse the same mutation as Add Member: assignUsersToTeam
  useApollo.team.addTeamMembers({
    userUids: remainingUserUids,
    teamUid,
    projectUid,
  }).then(res => {
    root.showSuccess(res.data.assignUsersToTeam.message)
    emit('updated')
  }).catch(err => {
    root.showErrorMessage(err)
  }).finally(() => {
    isProcessing.value = false
    root.$bvModal.hide('team-remove-member-modal')
  })
}
</script>

<style scoped>
.btn-icon-sm {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
